import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Users, BookOpen, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { COURSES, CREDENTIALS, COHORT_PHOTOS, CERT_BADGES } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const badgeColors: Record<string, string> = {
  gold: "bg-[#c8a96e] text-[#2c1f0e]",
  brown: "bg-[#2c1f0e] text-[#c8a96e] border border-[#c8a96e]/40",
  green: "bg-[#2d4a2d] text-[#8bc88b] border border-[#5a8a5a]/40",
  rose: "bg-[#4a1a2a] text-[#e8a0b0] border border-[#8a4a5a]/40",
};

// Auto-advancing cohort carousel
function CohortCarousel() {
  const [idx, setIdx] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = (n: number) => {
    setIdx((prev) => (prev + n + COHORT_PHOTOS.length) % COHORT_PHOTOS.length);
  };

  useEffect(() => {
    timer.current = setInterval(() => go(1), 4000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  return (
    <div className="relative overflow-hidden rounded-3xl shadow-2xl aspect-video max-w-4xl mx-auto">
      {COHORT_PHOTOS.map((photo, i) => (
        <div
          key={photo.src}
          className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          <img src={photo.src} alt={photo.label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2c1f0e]/60 to-transparent" />
          <div className="absolute bottom-5 left-6 text-[#f0e6d3] text-sm font-medium tracking-wide">{photo.label}</div>
        </div>
      ))}
      {/* Controls */}
      <button onClick={() => go(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-[#2c1f0e]/60 hover:bg-[#2c1f0e]/80 text-[#c8a96e] rounded-full p-2 transition-all">
        <ChevronLeft size={20} />
      </button>
      <button onClick={() => go(1)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-[#2c1f0e]/60 hover:bg-[#2c1f0e]/80 text-[#c8a96e] rounded-full p-2 transition-all">
        <ChevronRight size={20} />
      </button>
      {/* Dots */}
      <div className="absolute bottom-5 right-6 z-20 flex gap-1.5">
        {COHORT_PHOTOS.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-[#c8a96e] w-5" : "bg-[#f0e6d3]/50"}`} />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="bg-[#fdfbf7] overflow-x-hidden">
      {/* ── HERO ─────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${COURSES[0].image})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c1f0e]/88 via-[#2c1f0e]/72 to-[#1a1208]/82" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 text-[18vw] font-serif text-[#c8a96e]/5 select-none pointer-events-none leading-none pr-4">ॐ</div>

        <div className="relative z-10 container mx-auto px-6 text-center text-white pt-24">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.4em] uppercase mb-6 font-light">
              Abhyaas - School of Yoga & Wellness™ · Hong Kong
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-5xl md:text-7xl leading-tight mb-6 text-[#fdfbf7]">
              瑜伽，<br />不是修身，<br /><span className="text-[#c8a96e]">而是修心。</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/80 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              由 E-RYT 500 導師 Virginia Law 主理，深根香港15年。我們的師資培訓課程，不只教你如何帶領體式，而是陪你找回自己與身體最本真的連結。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/courses" className="inline-flex items-center gap-2 bg-[#c8a96e] text-[#2c1f0e] px-8 py-4 rounded-full font-semibold text-base hover:bg-[#d4b87c] transition-all duration-200 shadow-lg hover:-translate-y-0.5">
                探索所有課程 <ArrowRight size={18} />
              </Link>
              <a href="https://abhyaas.thinkific.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#c8a96e]/60 text-[#c8a96e] px-8 py-4 rounded-full font-semibold text-base hover:bg-[#c8a96e]/10 transition-all duration-200">
                學員登入平台
              </a>
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-[#c8a96e]/50" />
        </div>
      </section>

      {/* ── STATS STRIP ─────────────────────────────── */}
      <section className="bg-[#2c1f0e] py-10">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: <Award size={22} />, num: "37", label: "屆 RYT200 師資培訓" },
              { icon: <Users size={22} />, num: "300+", label: "已認證畢業導師" },
              { icon: <Star size={22} />, num: "15+", label: "年全職教學經驗" },
              { icon: <BookOpen size={22} />, num: "E-RYT 500", label: "最高級別國際認證" },
            ].map((s) => (
              <motion.div key={s.label} variants={fadeUp} className="flex flex-col items-center gap-2">
                <div className="text-[#c8a96e]">{s.icon}</div>
                <div className="text-[#c8a96e] font-serif text-3xl md:text-4xl font-bold">{s.num}</div>
                <div className="text-[#f0e6d3]/60 text-xs tracking-wide">{s.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FLAGSHIP COURSE ─────────────────────────── */}
      <section className="py-24 bg-[#fdfbf7]">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-6xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">旗艦課程 · 進行中</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl text-[#2c1f0e] text-center mb-16">RYT200 瑜伽師資培訓</motion.h2>
            <motion.div variants={fadeUp} className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ boxShadow: "0 24px 60px -12px rgba(44,31,14,0.25)" }}>
              <div className="grid md:grid-cols-2">
                <div className="relative h-72 md:h-auto min-h-[360px]">
                  <img src={COURSES[0].image} alt="RYT200 課程" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#2c1f0e]/30" />
                </div>
                <div className="bg-[#2c1f0e] p-10 md:p-14 flex flex-col justify-center">
                  <span className="inline-block bg-[#c8a96e] text-[#2c1f0e] text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6 w-fit">旗艦課程</span>
                  <h3 className="font-serif text-3xl text-[#fdfbf7] mb-4 leading-snug">200小時系統師資培訓</h3>
                  <p className="text-[#f0e6d3]/70 leading-relaxed mb-6">Yoga Alliance 國際認可，HKU SPACE 主辦。逾37屆、300+位畢業生見證。體式學、解剖學、哲學、教學方法論——一切從根本出發。</p>
                  <ul className="space-y-2 mb-6">
                    {COURSES[0].details.map((d) => (
                      <li key={d} className="flex items-center gap-2 text-[#f0e6d3]/80 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#c8a96e] flex-shrink-0" />{d}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col gap-2 mb-8">
                    <div className="flex items-center gap-3 p-3 bg-[#c8a96e]/10 rounded-xl border border-[#c8a96e]/20">
                      <div className="w-2 h-2 rounded-full bg-[#4ade80] animate-pulse flex-shrink-0" />
                      <span className="text-[#c8a96e] text-sm font-semibold">2026/27學年 · 已於5月23日正式開課（進行中）</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-[#ffffff]/5 rounded-xl border border-[#c8a96e]/15">
                      <span className="text-[#f0e6d3]/60 text-xs">主辦機構：HKU SPACE 香港大學持續進修學院</span>
                    </div>
                  </div>
                  <a href="https://abhyaas.thinkific.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#c8a96e] text-[#2c1f0e] px-8 py-4 rounded-full font-bold hover:bg-[#d4b87c] transition-all duration-200 w-full md:w-auto">
                    進入學員專區 <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── ALL COURSES GRID ─────────────────────────── */}
      <section className="py-20 bg-[#f5f0e8]">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-6xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">所有課程</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-[#2c1f0e] text-center mb-12">九大師資培訓課程</motion.h2>
            <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {COURSES.slice(1).map((course) => (
                <motion.div key={course.id} variants={fadeUp}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col"
                  style={{ boxShadow: "0 4px 20px -4px rgba(44,31,14,0.08)" }}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2c1f0e]/40 to-transparent" />
                    <span className={`absolute top-3 left-3 text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full ${badgeColors[course.badgeColor]}`}>{course.badge}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif text-lg text-[#2c1f0e] mb-2 leading-snug">{course.title}</h3>
                    <p className="text-[#5c4033]/70 text-sm leading-relaxed mb-4 flex-1">{course.description}</p>
                    <div className="text-[#8a7060] text-xs mb-4 font-medium">{course.nextDate}</div>
                    <a href={course.link} className="inline-flex items-center justify-center gap-1.5 border border-[#2c1f0e] text-[#2c1f0e] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2c1f0e] hover:text-[#c8a96e] transition-all duration-200">
                      查詢課程 <ArrowRight size={14} />
                    </a>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            <motion.div variants={fadeUp} className="text-center mt-10">
              <Link to="/courses" className="inline-flex items-center gap-2 text-[#2c1f0e] border-b border-[#c8a96e] pb-1 text-sm font-semibold hover:text-[#c8a96e] transition-colors">
                查看所有課程詳情 <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── COHORT SLIDESHOW ─────────────────────────── */}
      <section className="py-20 bg-[#fdfbf7]">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-5xl mx-auto">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4 text-center">歷屆學員</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-[#2c1f0e] text-center mb-4">37屆 · 300+位同行者</motion.h2>
            <motion.p variants={fadeUp} className="text-[#5c4033]/70 text-center mb-12 max-w-xl mx-auto">
              每一位走進 Abhyaas 的學員，都帶著自己的故事。這裡記錄了我們一起走過的時光。
            </motion.p>
            <motion.div variants={fadeUp}>
              <CohortCarousel />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CERT BADGES ─────────────────────────────── */}
      <section className="py-10 bg-[#f5f0e8] border-y border-[#c8a96e]/15">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {CERT_BADGES.map((b) => (
              <motion.div key={b.label} variants={fadeUp} className="flex flex-col items-center gap-2">
                <img src={b.src} alt={b.label} className="h-16 md:h-20 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
                <span className="text-[#8a7060] text-xs tracking-widest uppercase">{b.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── ABOUT TEASER ─────────────────────────────── */}
      <section className="py-24 bg-[#fdfbf7]">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} className="order-2 md:order-1">
              <p className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4">關於 Virginia</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#2c1f0e] mb-6 leading-tight">
                瑜伽不是目的地，<br /><span className="text-[#c8a96e]">而是同行的旅程。</span>
              </h2>
              <p className="text-[#5c4033]/80 leading-relaxed mb-6">
                2002年，我尋找内心真實的樣子，追求情緒的平穩與內心的安宜，踏上了瑜伽的旅程。此後15年，我從學生成為導師，創辦 Abhyaas，培育了300多位畢業生。
              </p>
              <p className="text-[#5c4033]/80 leading-relaxed mb-8">
                我深信，最好的老師不是站在台上的權威，而是願意與你一起走的同路人。
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 bg-[#2c1f0e] text-[#c8a96e] px-8 py-3.5 rounded-full font-semibold hover:bg-[#3d2b1a] transition-colors duration-200">
                了解更多 <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="order-1 md:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden aspect-[3/4] max-w-sm mx-auto shadow-2xl">
                <img
                  src="https://skyagent-artifacts.skywork.ai/router/agent/2026-05-18/prod_agent_a846de8d-e74f-4935-a1af-f907b9b64fb4/Bharadvajasana%20II%20-%20Yogaroot_8077_fb26a38f23e642c6a090aa4d59352e64.jpg"
                  alt="Virginia Law"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2c1f0e]/50 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[#fdfbf7] font-serif text-xl">Virginia Law</div>
                  <div className="text-[#c8a96e] text-xs tracking-widest uppercase mt-1">E-RYT 500 · RPYT · YACEP</div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#c8a96e]/10 blur-2xl" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CREDENTIALS STRIP ─────────────────────────── */}
      <section className="bg-[#2c1f0e] py-14">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CREDENTIALS.map((c) => (
              <motion.div key={c.label} variants={fadeUp} className="bg-[#3d2b1a]/60 rounded-2xl p-4 border border-[#c8a96e]/15 text-center">
                <div className="text-[#c8a96e] font-serif text-xl font-bold mb-1">{c.label}</div>
                <div className="text-[#f0e6d3]/50 text-xs leading-snug">{c.desc}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── KNOWLEDGE TEASER ─────────────────────────── */}
      <section className="py-20 bg-[#f5f0e8]">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-4xl mx-auto text-center">
            <motion.p variants={fadeUp} className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4">免費知識庫</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl text-[#2c1f0e] mb-4">瑜伽知識庫 · 冷知識與文章</motion.h2>
            <motion.p variants={fadeUp} className="text-[#5c4033]/70 mb-8 max-w-xl mx-auto">
              哲學、解剖、情緒管理、練習指引——免費開放閱讀。瑜伽的智慧，不應只在課室裡。
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/knowledge" className="inline-flex items-center gap-2 bg-[#2c1f0e] text-[#c8a96e] px-8 py-3.5 rounded-full font-semibold hover:bg-[#3d2b1a] transition-colors">
                進入知識庫 <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── BOOK CTA ─────────────────────────────────── */}
      <section className="py-20 bg-[#fdfbf7]">
        <div className="container mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mx-auto text-center">
            <p className="text-[#c8a96e] text-xs tracking-[0.3em] uppercase mb-4">著作</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2c1f0e] mb-6">《生活即修行：情緒急救包》</h2>
            <p className="text-[#5c4033]/80 leading-relaxed mb-4">
              53個章節，五大情緒主題——憤怒、悲傷、自我批評、關係與身體。這不是一本教你「管理」情緒的書，而是邀請你與情緒同行。
            </p>
            <p className="text-[#8a7060] text-sm mb-8">速熊文化 出版</p>
            <a href="#contact" className="inline-flex items-center gap-2 border border-[#2c1f0e] text-[#2c1f0e] px-8 py-3.5 rounded-full font-semibold hover:bg-[#2c1f0e] hover:text-[#c8a96e] transition-all duration-200">
              查詢購書 <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── CONTACT CTA ─────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-[#2c1f0e] to-[#1a1208]" id="contact-cta">
        <div className="container mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto">
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl text-[#fdfbf7] mb-6">準備好開始你的瑜伽旅程？</motion.h2>
            <motion.p variants={fadeUp} className="text-[#f0e6d3]/70 mb-8 leading-relaxed">
              無論是想成為導師，還是深化自己的修行，歡迎與我們聯絡。每一段旅程，都值得被認真對待。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <a href="mailto:abhyaas.hk@gmail.com" className="inline-flex items-center gap-2 bg-[#c8a96e] text-[#2c1f0e] px-8 py-4 rounded-full font-bold hover:bg-[#d4b87c] transition-colors">
                傳送電郵查詢
              </a>
              <a href="https://wa.me/85292935939" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#c8a96e]/60 text-[#c8a96e] px-8 py-4 rounded-full font-semibold hover:bg-[#c8a96e]/10 transition-colors">
                WhatsApp 聯絡
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
